import { useMemo, useState, useCallback, useEffect } from 'react';
import FlythroughRenderLibraryPickerModal from './FlythroughRenderLibraryPickerModal';
import FlythroughCameraAngleSequence from './FlythroughCameraAngleSequence';
import { FlythroughHistoryGrid } from './SavedFlythroughHistoryStrip';
import {
  FLYTHROUGH_CAMERA_PRESET_OPTIONS,
  FLYTHROUGH_DURATION_DEFAULT_SECONDS,
  FLYTHROUGH_DURATION_MIN_SECONDS,
  FLYTHROUGH_MOTION_MODES,
  getFlythroughDurationMaxSeconds,
  resolveFlythroughDurationForCameraAngleSequence,
  FLYTHROUGH_FRAME_RATE_OPTIONS,
  FLYTHROUGH_MAX_RENDER_SELECTION,
  FLYTHROUGH_MOTION_PROMPT_FIELDS,
  FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT,
  FLYTHROUGH_RESOLUTION_OPTIONS,
  FLYTHROUGH_ROI_FULL_SCREEN,
  FLYTHROUGH_ROI_MODE_OPTIONS,
  clampFlythroughRoi,
} from '../utils/photorealistic/flythroughVideoConfig';
import { FLYTHROUGH_CANONICAL_CAMERA_ANGLE } from '../utils/photorealistic/flythroughCameraAngles';

const pillBaseClass =
  'rounded-full border px-3 py-2 text-center text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300/60';

const sectionLabelClass =
  'text-center text-[11px] font-semibold tracking-wide text-gray-500';

const flythroughSliderShellClass =
  'flex w-full items-center rounded-full bg-white h-[36px] px-3 py-1.5';

function PreferencesIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function OptionPillButton({ selected, onClick, label, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${pillBaseClass} ${className} ${
        selected
          ? 'border-gray-800 bg-gray-800 text-white'
          : 'border-gray-200 bg-white text-gray-800 hover:border-primary-300 hover:bg-primary-500/10'
      }`}
    >
      {label}
    </button>
  );
}

function OptionPillGrid({ label, options, value, onChange, name }) {
  return (
    <div className="flex flex-col gap-2">
      <span className={sectionLabelClass}>{label}</span>
      <div className="grid grid-cols-2 gap-1.5" role="radiogroup" aria-label={label}>
        {options.map((option) => (
          <OptionPillButton
            key={option.id}
            selected={value === option.id}
            label={option.label}
            onClick={() => onChange(option.id)}
            className="w-full"
          />
        ))}
      </div>
    </div>
  );
}

function getSliderFillPercent(value, min, max) {
  if (max <= min) return 0;
  const normalized = (Number(value) - min) / (max - min);
  return Math.max(0, Math.min(100, normalized * 100));
}

function LabeledSlider({
  label,
  value,
  min,
  max,
  step,
  formatValue,
  disabled = false,
  onChange,
}) {
  const fillPercent = getSliderFillPercent(value, min, max);

  return (
    <div className={`flex flex-col gap-1.5 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-between gap-2 text-[11px]">
        <span className="font-medium text-gray-600">{label}</span>
        <span className="tabular-nums text-gray-500">{formatValue(value)}</span>
      </div>
      <div className={flythroughSliderShellClass}>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(Number(event.target.value))}
          className="brush-size-slider min-w-0 w-full shrink-0 disabled:cursor-not-allowed"
          style={{ '--brush-fill': `${fillPercent}%` }}
          aria-label={label}
        />
      </div>
    </div>
  );
}

function CameraPresetGroup({ value, onChange, hideLabel = false }) {
  return (
    <div className="flex flex-col gap-2">
      {hideLabel ? null : (
        <span className={sectionLabelClass}>Camera Preset</span>
      )}
      <div className="grid grid-cols-2 gap-1.5" role="radiogroup" aria-label="Camera Preset">
        {FLYTHROUGH_CAMERA_PRESET_OPTIONS.map((option) => (
          <OptionPillButton
            key={option.id}
            selected={value === option.id}
            label={option.label}
            onClick={() => onChange(option.id)}
            className="w-full"
          />
        ))}
      </div>
    </div>
  );
}

const SINGLE_KEYFRAME_MOTION_TABS = [
  { id: FLYTHROUGH_MOTION_MODES.CAMERA_PRESET, label: 'Camera preset' },
  { id: FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE, label: 'Camera angles' },
  { id: FLYTHROUGH_MOTION_MODES.PROMPT, label: 'Prompt' },
];

function MotionPromptFields({
  motionPromptFields = FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT,
  onMotionPromptFieldChange,
}) {
  return (
    <div className="flex flex-col gap-2">
      {FLYTHROUGH_MOTION_PROMPT_FIELDS.map((field) => (
        <label key={field.id} className="flex flex-col gap-1">
          <span className="text-[10px] font-medium text-gray-600">{field.label}</span>
          <input
            type="text"
            value={motionPromptFields[field.id] ?? ''}
            onChange={(event) => onMotionPromptFieldChange?.(field.id, event.target.value)}
            placeholder={field.placeholder}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm placeholder:text-gray-400 focus:border-gray-300 focus:outline-none focus:ring-0"
          />
        </label>
      ))}
    </div>
  );
}

function MotionSection({
  motionMode = FLYTHROUGH_MOTION_MODES.CAMERA_PRESET,
  onMotionModeChange,
  cameraPreset,
  onCameraPresetChange,
  cameraAngleSequence = [FLYTHROUGH_CANONICAL_CAMERA_ANGLE],
  onCameraAngleSequenceChange,
  draftCameraAngle = FLYTHROUGH_CANONICAL_CAMERA_ANGLE,
  onDraftCameraAngleChange,
  motionPromptFields = FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT,
  onMotionPromptFieldChange,
  keyframeCount = 1,
}) {
  const isKeyframeStitching = keyframeCount >= 2;

  return (
    <div className="flex flex-col gap-2">
      <span className={sectionLabelClass}>Motion</span>

      {isKeyframeStitching ? (
        <div className="rounded-xl border border-gray-200 bg-white px-2 py-3 text-center text-[10px] text-gray-600">
          {keyframeCount} keyframes selected. Transition videos will be stitched between each
          consecutive pair.
        </div>
      ) : (
        <>
          <div className="flex shrink-0 gap-1" role="tablist" aria-label="Motion">
            {SINGLE_KEYFRAME_MOTION_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={motionMode === tab.id}
                onClick={() => onMotionModeChange?.(tab.id)}
                className={`flex flex-1 items-center justify-center rounded-full border px-1 py-1.5 text-[9px] font-medium leading-tight transition-colors ${
                  motionMode === tab.id
                    ? 'border-gray-800 bg-gray-800 text-white'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div
            className={
              motionMode === FLYTHROUGH_MOTION_MODES.CAMERA_PRESET ? 'flex flex-col gap-2' : 'hidden'
            }
          >
            <CameraPresetGroup
              value={cameraPreset}
              onChange={onCameraPresetChange}
              hideLabel
            />
          </div>

          <div
            className={
              motionMode === FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE
                ? 'flex flex-col gap-2'
                : 'hidden'
            }
          >
            <FlythroughCameraAngleSequence
              sequence={cameraAngleSequence}
              onSequenceChange={onCameraAngleSequenceChange}
              draftAngle={draftCameraAngle}
              onDraftAngleChange={onDraftCameraAngleChange}
            />
          </div>

          <div
            className={
              motionMode === FLYTHROUGH_MOTION_MODES.PROMPT ? 'flex flex-col gap-2' : 'hidden'
            }
          >
            <MotionPromptFields
              motionPromptFields={motionPromptFields}
              onMotionPromptFieldChange={onMotionPromptFieldChange}
            />
          </div>
        </>
      )}
    </div>
  );
}

const FlythroughVideoPanel = ({
  libraryItems = [],
  libraryLoading = false,
  libraryError = null,
  onLoadLibrary,
  roiMode = 'full_screen',
  onRoiModeChange,
  roi = FLYTHROUGH_ROI_FULL_SCREEN,
  onRoiChange,
  cameraPreset = 'dolly_in',
  onCameraPresetChange,
  motionMode = FLYTHROUGH_MOTION_MODES.CAMERA_PRESET,
  onMotionModeChange,
  cameraAngleSequence = [FLYTHROUGH_CANONICAL_CAMERA_ANGLE],
  onCameraAngleSequenceChange,
  draftCameraAngle = FLYTHROUGH_CANONICAL_CAMERA_ANGLE,
  onDraftCameraAngleChange,
  motionPromptFields = FLYTHROUGH_MOTION_PROMPT_FIELDS_DEFAULT,
  onMotionPromptFieldChange,
  durationSeconds = FLYTHROUGH_DURATION_DEFAULT_SECONDS,
  onDurationSecondsChange,
  renderResolution = '432p',
  onRenderResolutionChange,
  frameRate = '24',
  onFrameRateChange,
  selectedRenderIds = [],
  onSelectedRenderIdsChange,
  libraryFlythroughs = [],
  libraryFlythroughsLoading = false,
  libraryFlythroughsError = null,
  onLoadFlythroughLibrary,
  onLibraryFlythroughSelect,
  onLibraryFlythroughDownload,
  onLibraryFlythroughDelete,
  deletingLibraryFlythroughId = null,
  downloadingLibraryFlythroughId = null,
}) => {
  const [activeTab, setActiveTab] = useState('preferences');
  const [renderPickerOpen, setRenderPickerOpen] = useState(false);

  useEffect(() => {
    if (activeTab === 'library') {
      onLoadFlythroughLibrary?.();
    }
  }, [activeTab, onLoadFlythroughLibrary]);

  const selectedRenders = useMemo(
    () =>
      selectedRenderIds
        .map((id) => libraryItems.find((item) => String(item?.id) === String(id)))
        .filter(Boolean),
    [libraryItems, selectedRenderIds],
  );

  const canAddMoreRenders = selectedRenders.length < FLYTHROUGH_MAX_RENDER_SELECTION;

  const handleRemoveSelectedRender = (itemId) => {
    onSelectedRenderIdsChange?.(
      selectedRenderIds.filter((id) => String(id) !== String(itemId)),
    );
  };

  const handleOpenRenderPicker = () => {
    onLoadLibrary?.();
    setRenderPickerOpen(true);
  };

  const handleRenderPickerConfirm = (nextIds) => {
    onSelectedRenderIdsChange?.(nextIds.map((id) => String(id)).slice(0, FLYTHROUGH_MAX_RENDER_SELECTION));
    setRenderPickerOpen(false);
  };

  const handleRenderPickerClose = useCallback(() => {
    setRenderPickerOpen(false);
  }, []);

  const durationMaxSeconds = useMemo(() => {
    if (motionMode === FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE) {
      return resolveFlythroughDurationForCameraAngleSequence(cameraAngleSequence.length);
    }
    return getFlythroughDurationMaxSeconds(motionMode);
  }, [motionMode, cameraAngleSequence.length]);
  const durationSliderDisabled =
    motionMode === FLYTHROUGH_MOTION_MODES.CAMERA_ANGLE_SEQUENCE;
  const effectiveDurationSeconds = useMemo(() => {
    if (durationSliderDisabled) {
      return resolveFlythroughDurationForCameraAngleSequence(cameraAngleSequence.length);
    }
    return durationSeconds;
  }, [durationSliderDisabled, cameraAngleSequence.length, durationSeconds]);

  useEffect(() => {
    if (!durationSliderDisabled) return;
    if (effectiveDurationSeconds === durationSeconds) return;
    onDurationSecondsChange?.(effectiveDurationSeconds);
  }, [
    durationSliderDisabled,
    effectiveDurationSeconds,
    durationSeconds,
    onDurationSecondsChange,
  ]);

  const roiSlidersDisabled = roiMode === 'full_screen';

  const handleRoiModeChange = (nextMode) => {
    onRoiModeChange?.(nextMode);
  };

  const updateRoiValue = (key, nextValue) => {
    onRoiChange?.(clampFlythroughRoi({ ...roi, [key]: nextValue }));
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="mb-3 shrink-0">
        <h3 className="text-[16px] lao-mn text-gray-700">Flythrough Video</h3>
      </div>

      <div className="mb-3 flex shrink-0 gap-1.5">
        <button
          type="button"
          onClick={() => setActiveTab('preferences')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-full border px-2 py-1.5 text-[11px] font-medium transition-colors ${
            activeTab === 'preferences'
              ? 'border-gray-800 bg-gray-800 text-white'
              : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
          }`}
        >
          <PreferencesIcon />
          Preferences
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('library')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-full border px-2 py-1.5 text-[11px] font-medium transition-colors ${
            activeTab === 'library'
              ? 'border-gray-800 bg-gray-800 text-white'
              : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
          }`}
        >
          <FolderIcon />
          Video Library
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain pr-0.5">
        {activeTab === 'preferences' ? (
          <div className="flex flex-col gap-4 px-0.5 py-1 pb-3">
            <p className="shrink-0 text-center text-sm font-semibold text-gray-700">Preferences</p>

            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-1.5">
                {selectedRenders.map((item) => {
                  const imageUrl = item?.imageUrl ?? item?.lightboxImageUrl;
                  return (
                    <div
                      key={String(item?.id)}
                      className="relative aspect-[4/3] w-[calc(33.333%-0.25rem)] min-w-[4.5rem] flex-1"
                    >
                      <div className="h-full w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt="Selected keyframe"
                            className="h-full w-full object-cover"
                            draggable={false}
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
                            No preview
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSelectedRender(item.id)}
                        className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-600 shadow-sm transition-colors hover:border-red-300 hover:text-red-600"
                        title="Remove keyframe"
                        aria-label="Remove keyframe"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="8"
                          height="8"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M18 6L6 18" />
                          <path d="M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
                {canAddMoreRenders ? (
                  <button
                    type="button"
                    onClick={handleOpenRenderPicker}
                    className={`flex aspect-[16/9] items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white text-gray-400 shadow-sm transition-colors hover:border-primary-300 hover:text-primary-300 ${
                      selectedRenders.length === 0 ? 'w-full' : 'w-[calc(33.333%-0.25rem)] min-w-[4.5rem] flex-1'
                    }`}
                    title="Add keyframe from library"
                    aria-label="Add keyframe from library"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M12 5v14" />
                      <path d="M5 12h14" />
                    </svg>
                  </button>
                ) : null}
              </div>
              <p className="text-center text-[10px] text-gray-500">
                {selectedRenders.length}/{FLYTHROUGH_MAX_RENDER_SELECTION} keyframes selected
                {selectedRenders.length === 0
                  ? ' · uses current room image'
                  : selectedRenders.length === 1
                    ? ' · combine with preset, prompt, or camera angles'
                    : selectedRenders.length >= 2
                      ? ' · stitching mode'
                      : ''}
              </p>
            </div>

            <MotionSection
              motionMode={motionMode}
              onMotionModeChange={onMotionModeChange}
              cameraPreset={cameraPreset}
              onCameraPresetChange={onCameraPresetChange}
              cameraAngleSequence={cameraAngleSequence}
              onCameraAngleSequenceChange={onCameraAngleSequenceChange}
              draftCameraAngle={draftCameraAngle}
              onDraftCameraAngleChange={onDraftCameraAngleChange}
              motionPromptFields={motionPromptFields}
              onMotionPromptFieldChange={onMotionPromptFieldChange}
              keyframeCount={selectedRenders.length}
            />

            <div className="flex flex-col gap-2">
              <span className={sectionLabelClass}>Flythrough ROI</span>
              <div className="grid grid-cols-2 gap-1.5" role="radiogroup" aria-label="Flythrough ROI">
                {FLYTHROUGH_ROI_MODE_OPTIONS.map((option) => (
                  <OptionPillButton
                    key={option.id}
                    selected={roiMode === option.id}
                    label={option.label}
                    onClick={() => handleRoiModeChange(option.id)}
                    className="w-full"
                  />
                ))}
              </div>
              <div className="mt-1 flex flex-col gap-2">
                <LabeledSlider
                  label="X (From left)"
                  value={roi.x}
                  min={0}
                  max={1}
                  step={0.01}
                  disabled={roiSlidersDisabled}
                  formatValue={(val) => val.toFixed(2)}
                  onChange={(nextValue) => updateRoiValue('x', nextValue)}
                />
                <LabeledSlider
                  label="Y (From top)"
                  value={roi.y}
                  min={0}
                  max={1}
                  step={0.01}
                  disabled={roiSlidersDisabled}
                  formatValue={(val) => val.toFixed(2)}
                  onChange={(nextValue) => updateRoiValue('y', nextValue)}
                />
                <LabeledSlider
                  label="Width"
                  value={roi.width}
                  min={0}
                  max={1}
                  step={0.01}
                  disabled={roiSlidersDisabled}
                  formatValue={(val) => val.toFixed(2)}
                  onChange={(nextValue) => updateRoiValue('width', nextValue)}
                />
                <LabeledSlider
                  label="Height"
                  value={roi.height}
                  min={0}
                  max={1}
                  step={0.01}
                  disabled={roiSlidersDisabled}
                  formatValue={(val) => val.toFixed(2)}
                  onChange={(nextValue) => updateRoiValue('height', nextValue)}
                />
              </div>
            </div>

            <div className={`flex flex-col gap-2 ${durationSliderDisabled ? 'opacity-50' : ''}`}>
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className="font-semibold tracking-wide text-gray-500">Duration</span>
                <span className="tabular-nums text-gray-500">{effectiveDurationSeconds}s</span>
              </div>
              <div className={flythroughSliderShellClass}>
                <input
                  key={`flythrough-duration-${durationMaxSeconds}-${effectiveDurationSeconds}`}
                  type="range"
                  min={FLYTHROUGH_DURATION_MIN_SECONDS}
                  max={durationMaxSeconds}
                  step={1}
                  value={effectiveDurationSeconds}
                  disabled={durationSliderDisabled}
                  onChange={(event) => onDurationSecondsChange?.(Number(event.target.value))}
                  className="brush-size-slider min-w-0 w-full shrink-0 disabled:cursor-not-allowed"
                  style={{
                    '--brush-fill': `${getSliderFillPercent(
                      effectiveDurationSeconds,
                      FLYTHROUGH_DURATION_MIN_SECONDS,
                      durationMaxSeconds,
                    )}%`,
                  }}
                  aria-label="Duration"
                />
              </div>
              <p className="text-center text-[10px] italic text-gray-500">
                {durationSliderDisabled
                  ? 'Duration stays at 4s for the first new angle, then increases by 4s for each additional angle.'
                  : `From ${FLYTHROUGH_DURATION_MIN_SECONDS}s to maximum ${durationMaxSeconds}s`}
              </p>
            </div>

            <OptionPillGrid
              label="Render resolution"
              options={FLYTHROUGH_RESOLUTION_OPTIONS}
              value={renderResolution}
              onChange={onRenderResolutionChange}
            />

            <OptionPillGrid
              label="Frame rate"
              options={FLYTHROUGH_FRAME_RATE_OPTIONS}
              value={frameRate}
              onChange={onFrameRateChange}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3 px-0.5 py-1 pb-3">
            <p className="shrink-0 text-center text-sm font-semibold text-gray-700">Video Library</p>
            {libraryFlythroughsLoading ? (
              <p className="text-center text-xs text-gray-500 py-8">Loading saved flythrough videos…</p>
            ) : libraryFlythroughsError ? (
              <p className="text-center text-xs text-red-600 py-8">{libraryFlythroughsError}</p>
            ) : !libraryFlythroughs.length ? (
              <p className="text-center text-xs text-gray-500 py-8">
                No saved flythrough videos yet. Generate a video and save it from Export Video.
              </p>
            ) : (
              <FlythroughHistoryGrid
                items={libraryFlythroughs}
                columns={2}
                onSelectItem={onLibraryFlythroughSelect}
                onDownloadItem={onLibraryFlythroughDownload}
                onDeleteItem={onLibraryFlythroughDelete}
                deletingItemId={deletingLibraryFlythroughId}
                downloadingItemId={downloadingLibraryFlythroughId}
              />
            )}
          </div>
        )}
      </div>

      <FlythroughRenderLibraryPickerModal
        open={renderPickerOpen}
        items={libraryItems}
        loading={libraryLoading}
        error={libraryError}
        selectedIds={selectedRenderIds}
        onClose={handleRenderPickerClose}
        onConfirm={handleRenderPickerConfirm}
      />
    </div>
  );
};

export default FlythroughVideoPanel;
